import styled, { keyframes } from 'styled-components';
import type { SigningVerdict } from './contractScoring';

const reveal = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

interface WrapProps {
  $tone: SigningVerdict['status'];
}

const Wrap = styled.div<WrapProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme, $tone }) =>
    $tone === 'green'
      ? theme.colors.status.success + '1a'
      : $tone === 'yellow'
        ? theme.colors.status.warning + '1a'
        : $tone === 'red'
          ? theme.colors.status.danger + '1a'
          : theme.colors.surface};
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'green'
        ? theme.colors.status.success + '55'
        : $tone === 'yellow'
          ? theme.colors.status.warning + '55'
          : $tone === 'red'
            ? theme.colors.status.danger + '55'
            : theme.colors.border};
  border-left: 4px solid
    ${({ theme, $tone }) =>
      $tone === 'green'
        ? theme.colors.status.success
        : $tone === 'yellow'
          ? theme.colors.status.warning
          : $tone === 'red'
            ? theme.colors.status.danger
            : theme.colors.text.muted};
  border-radius: ${({ theme }) => theme.radius.md};
  animation: ${reveal} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

const Light = styled.div<WrapProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ theme, $tone }) =>
    $tone === 'green'
      ? theme.colors.status.success
      : $tone === 'yellow'
        ? theme.colors.status.warning
        : $tone === 'red'
          ? theme.colors.status.danger
          : theme.colors.text.muted};
  color: white;
  font-size: 22px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 0 18px
    ${({ theme, $tone }) =>
      $tone === 'green'
        ? theme.colors.status.success + '66'
        : $tone === 'yellow'
          ? theme.colors.status.warning + '66'
          : $tone === 'red'
            ? theme.colors.status.danger + '66'
            : 'transparent'};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div<WrapProps>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme, $tone }) =>
    $tone === 'green'
      ? theme.colors.status.success
      : $tone === 'yellow'
        ? theme.colors.status.warning
        : $tone === 'red'
          ? theme.colors.status.danger
          : theme.colors.text.primary};
  margin-bottom: 2px;
`;

const Reason = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const Stats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2.5)};
  margin-top: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Stat = styled.span<{ $tone: 'danger' | 'warning' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme, $tone }) =>
      $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'warning'
          ? theme.colors.status.warning
          : theme.colors.text.muted};
  }
`;

const ICON: Record<SigningVerdict['status'], string> = {
  green: '✓',
  yellow: '!',
  red: '✕',
  pending: '…',
  empty: '?',
};

interface Props {
  verdict: SigningVerdict;
}

export function SigningVerdictBanner({ verdict }: Props) {
  return (
    <Wrap $tone={verdict.status}>
      <Light $tone={verdict.status}>{ICON[verdict.status]}</Light>
      <Body>
        <Title $tone={verdict.status}>{verdict.label}</Title>
        <Reason>{verdict.reason}</Reason>
        {verdict.active_critical + verdict.active_major + verdict.active_minor > 0 && (
          <Stats>
            <Stat $tone="danger">
              Критические: <strong>{verdict.active_critical}</strong>
            </Stat>
            <Stat $tone="warning">
              Важные: <strong>{verdict.active_major}</strong>
            </Stat>
            <Stat $tone="neutral">
              Незначительные: <strong>{verdict.active_minor}</strong>
            </Stat>
          </Stats>
        )}
      </Body>
    </Wrap>
  );
}
